import React, { useState } from "react";
import { AssociatedPersonsProps } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import {
    getRiskBadgeClass,
    getVerificationBadgeClass,
    roleColorClass,
    roleLabel
} from "./associatedPersonsConfig";

export const AssociatedPersons: React.FC<AssociatedPersonsProps> = ({
  persons,
  title = "Associated Persons",
  onPersonClick,
}) => {
  const [expandedPersons, setExpandedPersons] = useState<Set<string>>(new Set());
  const [filterRole, setFilterRole] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Get unique roles for filter
  const allRoles = Array.from(new Set(persons.flatMap((p) => p.roles)));

  // Filter persons
  const filteredPersons = persons.filter((person) => {
    const matchesRole =
      filterRole === "all" || person.roles.includes(filterRole as any);
    const matchesSearch =
      searchTerm === "" ||
      `${person.firstName} ${person.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const toggleExpand = (personId: string) => {
    setExpandedPersons((prev) => {
      const next = new Set(prev);
      if (next.has(personId)) {
        next.delete(personId);
      } else {
        next.add(personId);
      }
      return next;
    });
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      {/* Header with gradient */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <span className="bg-white/20 text-white text-sm font-medium px-3 py-1 rounded-full backdrop-blur-sm">
            {filteredPersons.length}{" "}
            {filteredPersons.length === 1 ? "Person" : "People"}
          </span>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>
          <div className="min-w-[180px]">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 rounded-lg text-sm text-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            >
              <option value="all">All Roles</option>
              {allRoles.filter(Boolean).map((role, idx) => (
                <option key={`${role}-${idx}`} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Quick Stats - Vibrant Gradient Cards */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Persons - Blue to Indigo */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-3 sm:p-5 border border-blue-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {persons.length}
                  </p>
                  <p className="text-sm font-semibold text-blue-100 mt-1">
                    Total Persons
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Verified - Emerald to Teal */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-5 border border-emerald-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {
                      persons.filter((p) => p.verificationStatus === "verified")
                        .length
                    }
                  </p>
                  <p className="text-sm font-semibold text-emerald-100 mt-1">
                    Verified
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* PEP Flagged - Purple to Pink */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 rounded-2xl p-3 sm:p-5 border border-purple-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {persons.filter((p) => p.isPEP).length}
                  </p>
                  <p className="text-sm font-semibold text-purple-100 mt-1">
                    PEP Flagged
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* High Risk - Amber to Orange */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 rounded-2xl p-3 sm:p-5 border border-amber-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {
                      persons.filter(
                        (p) =>
                          p.riskLevel === "high" || p.riskLevel === "critical",
                      ).length
                    }
                  </p>
                  <p className="text-sm font-semibold text-amber-100 mt-1">
                    High Risk
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Person List */}
      <div className="divide-y divide-slate-200/60">
        {filteredPersons.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 mb-4">
              <svg
                className="h-8 w-8 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <p className="text-slate-500 font-medium">
              No associated persons found matching your criteria.
            </p>
          </div>
        ) : (
          filteredPersons.map((person) => (
            <div
              key={person.id}
              className="hover:bg-gradient-to-r hover:from-slate-50/50 hover:to-transparent transition-all duration-200"
            >
              {/* Person Summary Row */}
              <div
                className="px-4 sm:px-6 py-4 sm:py-5 cursor-pointer"
                onClick={() => toggleExpand(person.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <span className="text-white font-semibold text-sm">
                        {person.firstName[0]}
                        {person.lastName[0]}
                      </span>
                    </div>

                    {/* Name and Roles */}
                    <div>
                      <h4
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPersonClick?.(person);
                        }}
                      >
                        {person.title && `${person.title} `}
                        {person.firstName}{" "}
                        {person.middleName && `${person.middleName} `}
                        {person.lastName}
                      </h4>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {person.roles.filter(Boolean).map((role, idx) => (
                          <span
                            key={`${role}-${idx}`}
                            className={`inline-block px-2 py-0.5 text-xs rounded-full ring-1 font-medium ${roleColorClass[role] || "bg-slate-100 text-slate-600 ring-slate-200"}`}
                          >
                            {roleLabel(role)}
                          </span>
                        ))}
                      </div>

                      {/*
                        AL-519 item 3 — authority to act, as a marker on the person
                        rather than a role chip.

                        Deliberately NOT in the chip row above. Authority is a
                        property of the person; Director and Beneficial Owner are
                        offices. Putting them side by side is what let a C1 read
                        "Authorised Representative" as an office and miss who was
                        actually authorised.
                      */}
                      {person.authorisedToAct === true && (
                        <p
                          className="mt-1.5 text-xs font-medium text-emerald-700"
                          data-testid={`authority-${person.id}`}
                        >
                          Authorised to act
                          {person.authorityBasis === "own-right" && (
                            <span className="font-normal text-slate-500">
                              {" "}
                              (officer of the entity)
                            </span>
                          )}
                          {person.authorityBasis === "delegated" && person.authorisedByName && (
                            <span className="font-normal text-slate-500">
                              {" "}
                              (by {person.authorisedByName})
                            </span>
                          )}
                          {/*
                            'unknown' renders the bare fact and NO basis. It must not
                            fall back to "by an external principal" — the gate can be
                            set by ASIC matching the person as a director, so a missing
                            self-claim row is missing information, not evidence of
                            delegation. Claiming otherwise is AL-445.
                          */}
                        </p>
                      )}

                      {/*
                        The Authorising Principal on their own record. This is the
                        fact the server's `isAuthorising` emit exists to deliver, and
                        without it the "(by X)" above names somebody the reader
                        cannot find in the list.
                      */}
                      {person.isAuthorisingPrincipal === true && (
                        <p
                          className="mt-1 text-xs font-medium text-indigo-700"
                          data-testid={`authorising-principal-${person.id}`}
                        >
                          Authorising Principal
                          <span className="font-normal text-slate-500">
                            {" "}
                            (attested the application contact may act)
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="flex items-center space-x-3">
                    {person.ownershipPercentage !== undefined ? (
                      <span className="text-sm text-gray-600">
                        {person.ownershipPercentage}% Ownership
                      </span>
                    ) : (
                      /* AL-521: the register and the beneficial-ownership declaration give different
                         figures. There is no honest single number for that, so show both with their
                         source rather than picking one or totalling them. */
                      person.ownership && (
                        <span className="text-sm text-gray-600">
                          Ownership:{" "}
                          {person.ownership.registeredPercentage !== null
                            ? `${person.ownership.registeredPercentage}% registered`
                            : "not on the register"}
                          {" · "}
                          {person.ownership.declaredPercentage !== null
                            ? `${person.ownership.declaredPercentage}% declared`
                            : "none declared"}
                        </span>
                      )
                    )}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${getVerificationBadgeClass(person.verificationStatus)}`}
                    >
                      {person.verificationStatus.replace("-", " ")}
                    </span>
                    {person.riskLevel && (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getRiskBadgeClass(person.riskLevel)}`}
                      >
                        {person.riskLevel} risk
                      </span>
                    )}
                    {person.isPEP && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-violet-100 to-purple-100 text-violet-800 ring-1 ring-violet-200">
                        PEP
                      </span>
                    )}
                    {person.isSanctioned && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 ring-1 ring-rose-200">
                        Sanctioned
                      </span>
                    )}
                    <svg
                      className={`w-5 h-5 text-gray-400 transform transition-transform ${expandedPersons.has(person.id) ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedPersons.has(person.id) && (
                <div className="px-6 pb-5 bg-gradient-to-br from-slate-50/80 via-white/50 to-slate-50/80">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-5 border-t border-slate-200/60">
                    {/* Personal Details */}
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/40">
                      <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3 tracking-wide">
                        Personal Details
                      </h5>
                      <dl className="space-y-1 text-sm">
                        {person.dateOfBirth && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Date of Birth:</dt>
                            <dd className="text-gray-900">
                              {formatDateShort(person.dateOfBirth)}
                            </dd>
                          </div>
                        )}
                        {person.nationality && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Nationality:</dt>
                            <dd className="text-gray-900">
                              {person.nationality}
                            </dd>
                          </div>
                        )}
                        {person.occupation && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Occupation:</dt>
                            <dd className="text-gray-900">
                              {person.occupation}
                            </dd>
                          </div>
                        )}
                        {person.appointmentDate && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Appointed:</dt>
                            <dd className="text-gray-900">
                              {formatDateShort(person.appointmentDate)}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    {/* Contact Details */}
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/40">
                      <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3 tracking-wide">
                        Contact Details
                      </h5>
                      <dl className="space-y-1 text-sm">
                        {person.contactDetails?.email && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Email:</dt>
                            <dd className="text-gray-900">
                              {person.contactDetails.email}
                            </dd>
                          </div>
                        )}
                        {person.contactDetails?.phone && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Phone:</dt>
                            <dd className="text-gray-900">
                              {person.contactDetails.phone}
                            </dd>
                          </div>
                        )}
                        {person.residentialAddress && (
                          <div>
                            <dt className="text-gray-500 mb-1">Address:</dt>
                            <dd className="text-gray-900">
                              {person.residentialAddress.line1}
                              {person.residentialAddress.line2 && (
                                <>, {person.residentialAddress.line2}</>
                              )}
                              <br />
                              {person.residentialAddress.suburb},{" "}
                              {person.residentialAddress.state}{" "}
                              {person.residentialAddress.postcode}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    {/* Identity Documents */}
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/40">
                      <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3 tracking-wide">
                        Identity Documents
                      </h5>
                      {person.identityDocuments &&
                      person.identityDocuments.length > 0 ? (
                        <ul className="space-y-2">
                          {person.identityDocuments.map((doc) => (
                            <li
                              key={doc.id}
                              className="flex items-center justify-between text-sm"
                            >
                              <span className="text-gray-900 capitalize">
                                {doc.type === "other" ? (doc.documentNumber ? `Document (${doc.documentNumber})` : "Document") : doc.type.replace("-", " ")}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs rounded ${
                                  doc.status === "verified"
                                    ? "bg-green-100 text-green-800"
                                    : doc.status === "pending"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : doc.status === "expired"
                                        ? "bg-orange-100 text-orange-800"
                                        : "bg-red-100 text-red-800"
                                }`}
                              >
                                {doc.status}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No documents on file
                        </p>
                      )}
                    </div>
                  </div>

                  {/* PEP/Sanctions Details */}
                  {(person.isPEP || person.isSanctioned) && (
                    <div className="mt-4 pt-4 border-t border-slate-200/60">
                      <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3 tracking-wide">
                        Risk Indicators
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {person.isPEP && person.pepDetails && (
                          <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-200/60">
                            <span className="text-xs font-semibold text-violet-800 uppercase tracking-wide">
                              PEP Details
                            </span>
                            <p className="text-sm text-violet-700 mt-1.5">
                              {person.pepDetails}
                            </p>
                          </div>
                        )}
                        {person.isSanctioned && person.sanctionDetails && (
                          <div className="p-4 bg-gradient-to-br from-rose-50 to-red-50 rounded-xl border border-rose-200/60">
                            <span className="text-xs font-semibold text-rose-800 uppercase tracking-wide">
                              Sanction Details
                            </span>
                            <p className="text-sm text-rose-700 mt-1.5">
                              {person.sanctionDetails}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AssociatedPersons;
